import React, { useState, useEffect, useContext } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import { useWebSocket } from './WebSocket';
import {
  Box, Grid, GridItem, Flex, Text, Button, 
  Table, Thead, Tbody, Tr, Th, Td, VStack, HStack, Heading,
  Divider, Icon, Avatar, Badge, Spinner, Tooltip,
  Menu, MenuButton, MenuList, MenuItem, useToast,
  Input, FormControl, FormLabel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Select
} from "@chakra-ui/react";
import HeaderMenus from './HeaderMenus';
import { FiUsers, FiMessageSquare, FiChevronUp, FiChevronDown, FiBriefcase, FiSettings } from 'react-icons/fi';

// Add to your imports at the top
import { 
  // Add these new imports
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  Legend
} from 'recharts';
import { FiActivity, FiAlertCircle, FiMoreVertical, FiLock, FiPlusCircle, FiMinusCircle, FiCheckCircle } from 'react-icons/fi';


function Admin() {
  const [activeSection, setActiveSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [chatDetails, setChatDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [newOrgName, setNewOrgName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [uploadingLogoId, setUploadingLogoId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which user's status is being updated
  const toast = useToast(); // Add toast for notifications

  // State for defaults modal
  const { isOpen: isDefaultsModalOpen, onOpen: onDefaultsModalOpen, onClose: onDefaultsModalClose } = useDisclosure();
  const [selectedOrgForDefaults, setSelectedOrgForDefaults] = useState(null);
  const [orgClients, setOrgClients] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgDefaultClientId, setOrgDefaultClientId] = useState('');
  const [userDefaultClientIds, setUserDefaultClientIds] = useState({});
  const [isDefaultsLoading, setIsDefaultsLoading] = useState(false);
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);
  const [initialOrgDefaultId, setInitialOrgDefaultId] = useState('');
  const [initialUserDefaults, setInitialUserDefaults] = useState({});


  const { accessToken } = useContext(AppContext);
  const { socket } = useWebSocket();

  const [processingMetrics, setProcessingMetrics] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('loading'); // 'healthy', 'warning', 'critical', 'loading'
  

  // Fixed color values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const sidebarBg = 'gray.50';
  const tableHeaderBg = 'gray.50';
  
  // Navigation items for the sidebar
  const navItems = [
    { id: 'dashboard', label: 'Processing Health', icon: FiActivity },
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'organizations', label: 'Organizations', icon: FiBriefcase },
    { id: 'chatDetails', label: 'Chat Details', icon: FiMessageSquare },
  ];

  // Function to handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };



// Function to update user role
const updateUserRole = async (userId, newRole) => {
  setUpdatingStatus(userId);
  
  try {
    const response = await callApi('/manage_user_role', accessToken, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        role: newRole
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update user role');
    }
    
    // Update the user in the local state
    setUsers(users.map(user => {
      if (user.id === userId) {
        return { ...user, role: newRole };
      }
      return user;
    }));
    
    toast({
      title: "Role updated",
      description: `User role changed to ${newRole || 'standard'}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
  } catch (error) {
    console.error('Error updating user role:', error);
    toast({
      title: "Error",
      description: "Failed to update user role",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setUpdatingStatus(null);
  }
};

// Function to update user authorization status
const updateUserAuthStatus = async (userId, newStatus) => {
  setUpdatingStatus(userId);
  
  try {
    const response = await callApi('/manage_user_authorization', accessToken, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        status: newStatus,
        reason: `Status changed to ${newStatus} by admin`
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update user status');
    }
    
    // Update the user in the local state
    setUsers(users.map(user => {
      if (user.id === userId) {
        return { ...user, auth_status: newStatus, auth_status_date: new Date().toISOString() };
      }
      return user;
    }));
    
    toast({
      title: "Status updated",
      description: `User status changed to ${newStatus}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
  } catch (error) {
    console.error('Error updating user status:', error);
    toast({
      title: "Error",
      description: "Failed to update user status",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setUpdatingStatus(null);
  }
};

// Get status badge color based on status
const getStatusBadgeProps = (status) => {
  switch(status) {
    case 'approved':
      return { colorScheme: 'green', icon: FiCheckCircle };
    case 'rejected':
      return { colorScheme: 'red', icon: FiLock };
    case 'pending':
    default:
      return { colorScheme: 'yellow', icon: FiAlertCircle };
  }
};



  const handleProcessingMetricsClick = async () => {
    setIsLoading(true);
    setError(null);
    setActiveSection('dashboard');
    
    try {
      const response = await callApi('/processing_metrics', accessToken);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch processing metrics');
      }
      
      const data = await response.json();
      setProcessingMetrics(data.metrics);
      
      // Determine processing health status
    } catch (error) {
      console.error('Error fetching processing metrics:', error);
      setError('Failed to load processing metrics. Please try again.');
      setProcessingStatus('critical');
    } finally {
      setIsLoading(false);
    }
  };
  
 
  
  // Update useEffect to load processing metrics on initial render
  useEffect(() => {
    handleProcessingMetricsClick();
  }, []);
  
  const ProcessingHealthDashboard = () => {
    if (!processingMetrics) {
      return (
        <Flex justify="center" align="center" height="200px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      );
    }
    
    // Get the metrics data and metadata
    const metrics = processingMetrics.metrics || processingMetrics;
    
    // Get the directory metadata if available
    const directoryLabels = processingMetrics.directory_metadata || {
      success: { label: 'Revedy Success' },
      exceptions: { label: 'Revedy Exceptions' },
      processed: { label: 'Zinnia Processed' },
      exception: { label: 'Zinnia Exceptions' },
      tech_incomplete: { label: 'Technical Issues' }
    };
    
    // Define chart colors
    const chartColors = {
      new_cases: "#3182CE",  // blue
      success: "#38A169",    // green
      exceptions: "#DD6B20", // orange
      processed: "#805AD5",  // purple
      exception: "#E53E3E",  // red
      tech_incomplete: "#718096" // gray
    };
    
    return (
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Heading size="md">Processing Volume Dashboard</Heading>
          <Button 
            colorScheme="blue" 
            size="sm" 
            onClick={handleProcessingMetricsClick} 
            isLoading={isLoading}
          >
            Refresh Metrics
          </Button>
        </Flex>
        
        {/* Processing Trend Chart */}
        <Box 
          p={4} 
          borderRadius="md" 
          borderWidth="1px" 
          borderColor={borderColor} 
          bg="white" 
          boxShadow="sm"
          height="400px"
          mb={6}
        >
          <Text fontSize="md" fontWeight="bold" mb={4}>Processing Volume Trends (Last 30 Days)</Text>
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => {
                  const d = new Date(date);
                  return `${d.getMonth()+1}/${d.getDate()}`;
                }}
              />
              <YAxis />
              <RechartsTooltip 
                formatter={(value, name, props) => {
                  // Use the directory labels if available
                  const label = directoryLabels[name]?.label || name;
                  return [value, label];
                }}
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
              />
              <Legend 
                formatter={(value) => directoryLabels[value]?.label || value} 
                verticalAlign="top"
                height={36}
              />
              <Line 
                type="monotone" 
                dataKey="new_cases" 
                stroke={chartColors.new_cases} 
                name="new_cases" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="success" 
                stroke={chartColors.success} 
                name="success" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="exceptions" 
                stroke={chartColors.exceptions} 
                name="exceptions" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="processed" 
                stroke={chartColors.processed} 
                name="processed" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="exception" 
                stroke={chartColors.exception} 
                name="exception" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="tech_incomplete" 
                stroke={chartColors.tech_incomplete} 
                name="tech_incomplete" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Detailed Metrics Table */}
        <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Date</Th>
                <Th isNumeric>New Cases</Th>
                <Th isNumeric>{directoryLabels.success.label}</Th>
                <Th isNumeric>{directoryLabels.exceptions.label}</Th>
                <Th isNumeric>{directoryLabels.processed.label}</Th>
                <Th isNumeric>{directoryLabels.exception.label}</Th>
                <Th isNumeric>{directoryLabels.tech_incomplete.label}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {metrics.slice().reverse().slice(0, 14).map((day, index) => (
                <Tr key={index}>
                  <Td>{new Date(day.date).toLocaleDateString()}</Td>
                  <Td isNumeric>{day.new_cases}</Td>
                  <Td isNumeric>{day.success || 0}</Td>
                  <Td isNumeric>{day.exceptions || 0}</Td>
                  <Td isNumeric>{day.processed || 0}</Td>
                  <Td isNumeric>{day.exception || 0}</Td>
                  <Td isNumeric>{day.tech_incomplete || 0}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    );
  };



  // Function to format date in a user-friendly way
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleViewOrgsButtonClick = async () => {
    setIsLoading(true);
    setError(null);
    setActiveSection('organizations');
    try {
      const response = await callApi('/admin/organizations', accessToken);
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
      toast({ title: "Error", description: err.message, status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      toast({ title: "Validation Error", description: "Organization name cannot be empty.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setIsLoading(true);
    try {
      const response = await callApi('/admin/organizations', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOrgName }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create organization');
      }
      toast({ title: "Success", description: `Organization "${newOrgName}" created.`, status: "success", duration: 3000, isClosable: true });
      setNewOrgName('');
      handleViewOrgsButtonClick(); // Refresh the list
    } catch (err) {
      setError(err.message);
      toast({ title: "Error", description: err.message, status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = async (orgId, file) => {
    if (!file) return;

    const allowedExtensions = ['png', 'jpg', 'jpeg', 'svg', 'webp'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid File Type",
        description: "Please select a PNG, JPG, JPEG, or SVG file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploadingLogoId(orgId);
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await callApi(`/admin/organizations/${orgId}/logo`, accessToken, {
        method: 'PUT',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload logo.');
      }

      toast({
        title: "Success",
        description: "Logo updated successfully. It may take a moment to reflect.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Upload Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingLogoId(null);
    }
  };

  const handleOpenDefaultsModal = async (org) => {
    setSelectedOrgForDefaults(org);
    setIsDefaultsLoading(true);
    onDefaultsModalOpen();

    try {
      const response = await callApi(`/admin/organizations/${org.id}/details`, accessToken);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch organization details');
      }

      setOrgClients(data.clients || []);
      setOrgUsers(data.users || []);
      
      const orgDefault = data.org_default_client_id || '';
      setOrgDefaultClientId(orgDefault);
      setInitialOrgDefaultId(orgDefault);

      const initialUsers = (data.users || []).reduce((acc, user) => {
        acc[user.id] = user.default_client_id || '';
        return acc;
      }, {});
      setUserDefaultClientIds(initialUsers);
      setInitialUserDefaults(initialUsers);

    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 5000, isClosable: true });
      onDefaultsModalClose(); // Close modal on error
    } finally {
      setIsDefaultsLoading(false);
    }
  };

  const handleSaveDefaults = async () => {
    setIsSavingDefaults(true);
    const promises = [];

    // 1. Check if the organization's default client has changed
    if (initialOrgDefaultId !== orgDefaultClientId) {
      promises.push(callApi(`/admin/organizations/${selectedOrgForDefaults.id}/default-client`, accessToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: orgDefaultClientId || null }),
      }));
    }

    // 2. Check which user-specific defaults have changed
    for (const userId in userDefaultClientIds) {
      const initialValue = initialUserDefaults[userId] || '';
      const currentValue = userDefaultClientIds[userId] || '';
      if (initialValue !== currentValue) {
        promises.push(callApi(`/admin/users/${userId}/default-client`, accessToken, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: currentValue || null }),
        }));
      }
    }

    if (promises.length === 0) {
      toast({ title: "No changes", description: "No default settings were modified.", status: "info", duration: 3000, isClosable: true });
      setIsSavingDefaults(false);
      onDefaultsModalClose();
      return;
    }

    try {
      const responses = await Promise.all(promises);
      const failedResponse = responses.find(res => !res.ok);

      if (failedResponse) {
        const errorData = await failedResponse.json();
        throw new Error(errorData.error || 'One or more updates failed.');
      }

      toast({ title: "Success", description: "Default settings saved successfully.", status: "success", duration: 3000, isClosable: true });
      onDefaultsModalClose();
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setIsSavingDefaults(false);
    }
  };

  const handleUserDefaultChange = (userId, clientId) => {
    setUserDefaultClientIds(prev => ({
      ...prev,
      [userId]: clientId,
    }));
  };


// Get sorted users
const getSortedUsers = () => {
  if (!sortConfig.key) {
    return users.sort(sortByRoleOrgAndEmail);
  }

  return [...users].sort((a, b) => {
    if (a[sortConfig.key] === null && b[sortConfig.key] === null) return 0;
    if (a[sortConfig.key] === null) return 1;
    if (b[sortConfig.key] === null) return -1;
    
    let comparison = 0;
    if (sortConfig.key === 'last_activity_date' || sortConfig.key === 'auth_status_date') {
      // For dates, compare as Date objects
      const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : null;
      const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : null;
      
      if (!dateA) return 1;
      if (!dateB) return -1;
      comparison = dateA - dateB;
    } else if (sortConfig.key === 'note_count' || sortConfig.key === 'case_count') {
      // For numeric values
      comparison = a[sortConfig.key] - b[sortConfig.key];
    } else {
      // For string values
      comparison = a[sortConfig.key]?.localeCompare(b[sortConfig.key] || '');
    }
    
    return sortConfig.direction === 'ascending' ? comparison : -comparison;
  });
};

  const handleViewUsersButtonClick = async () => {
    setIsLoading(true);
    setError(null);
    setActiveSection('users');
    
    try {
      const response = await callApi('/admin/get_user_list', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setUsers(data.user_list);
        setIsImpersonating(data.is_impersonating);
        setSelectedUser(data.impersonated_user);
      } else {
        // If the backend hasn't been updated to the new format yet
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStartImpersonation = async (userId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await callApi('/start_impersonation', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ impersonated_user_id: userId }),
      });
      
      if (response.ok) {
        setIsImpersonating(true);
        setSelectedUser(users.find(user => user.id === userId));
        // Refresh the user list to get updated impersonation status
        handleViewUsersButtonClick();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start impersonation');
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      setError('Failed to start impersonation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStopImpersonation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await callApi('/stop_impersonation', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setIsImpersonating(false);
        setSelectedUser(null);
        // Refresh the user list to get updated impersonation status
        handleViewUsersButtonClick();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to stop impersonation');
      }
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      setError('Failed to stop impersonation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatDetailsButtonClick = async () => {
    setIsLoading(true);
    setError(null);
    setActiveSection('chatDetails');
    
    try {
      const response = await callApi('/admin_ionm_chat', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch chat details');
      }
      
      const data = await response.json();
      setChatDetails(data);
    } catch (error) {
      console.error('Error fetching chat details:', error);
      setError('Failed to load chat details. Please try again.');
      setChatDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const sortByRoleOrgAndEmail = (a, b) => {
    // Sort by role
    if (a.role === null && b.role === null) {
      // If both roles are null, proceed to org sorting
    } else if (a.role === null) return 1;
    else if (b.role === null) return -1;
    else if (a.role !== b.role) {
      return b.role.localeCompare(a.role);
    }
    
    // Sort by org
    const isADemo = a.org_name?.startsWith('Demo');
    const isBDemo = b.org_name?.startsWith('Demo');
    if (isADemo && !isBDemo) return 1;
    if (!isADemo && isBDemo) return -1;
    if (a.org_name !== b.org_name) {
      return a.org_name?.localeCompare(b.org_name);
    }
    
    // If roles and orgs are the same, sort by email
    return a.email?.localeCompare(b.email);
  };

  // Load users on initial render
  useEffect(() => {
    handleViewUsersButtonClick();
  }, []);

  // Render sort indicator
  const renderSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return null;
    }
    return sortConfig.direction === 'ascending' ? <FiChevronUp /> : <FiChevronDown />;
  };

  return (
    <Box>
      <HeaderMenus />
      
      <Grid
        templateColumns="250px 1fr"
        h="calc(100vh - 60px)"
      >
        {/* Sidebar */}
        <GridItem 
          bg={sidebarBg} 
          borderRight="1px" 
          borderColor={borderColor}
          p={4}
          overflowY="auto"
        >
          <VStack spacing={2} align="stretch">
            <Heading size="md" mb={4}>Admin Dashboard</Heading>
            
            {navItems.map(item => (
              <Button
                key={item.id}
                leftIcon={<Icon as={item.icon} />}
                justifyContent="flex-start"
                variant={activeSection === item.id ? "solid" : "ghost"}
                colorScheme={activeSection === item.id ? "blue" : "gray"}
                onClick={() => {
                  if (item.id === 'dashboard') handleProcessingMetricsClick();
                  if (item.id === 'users') handleViewUsersButtonClick();
                  if (item.id === 'organizations') handleViewOrgsButtonClick();
                  if (item.id === 'chatDetails') handleChatDetailsButtonClick();
                }}
                mb={1}
              >
                {item.label}
              </Button>
            ))}
          </VStack>
        </GridItem>

        {/* Main Content */}
        <GridItem 
          bg={bgColor} 
          p={6} 
          overflowY="auto"
        >
          {/* Error Message (if any) */}
          {error && (
            <Box p={4} mb={4} borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50">
              <Text color="red.600">{error}</Text>
            </Box>
          )}
          
          {/* Loading Indicator */}
          {isLoading && (
            <Flex justify="center" align="center" height="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          )}

          {/* Processing Health Dashboard */}
          {!isLoading && activeSection === 'dashboard' && (
            <ProcessingHealthDashboard />
          )}

          {/* Organizations Section */}
          {!isLoading && activeSection === 'organizations' && (
            <Box>
              <Heading size="md" mb={4}>Organization Management</Heading>
              
              {/* Create Org Form */}
              <Box as="form" onSubmit={handleCreateOrg} p={4} mb={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>New Organization Name</FormLabel>
                    <Input 
                      placeholder="Enter organization name"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                    />
                  </FormControl>
                  <Button type="submit" colorScheme="blue" isLoading={isLoading} alignSelf="flex-start">
                    Create Organization
                  </Button>
                </VStack>
              </Box>

              {/* Orgs Table */}
              <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>ID</Th>
                      <Th>Name</Th>
                      <Th>Created At</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {organizations.map((org) => (
                      <Tr key={org.id}>
                        <Td>{org.id}</Td>
                        <Td>{org.name}</Td>
                        <Td>{new Date(org.created_at).toLocaleDateString()}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Input
                              type="file"
                              id={`logo-upload-${org.id}`}
                              style={{ display: 'none' }}
                              accept=".png,.jpg,.jpeg,.svg,.webp"
                              onChange={(e) => handleLogoUpload(org.id, e.target.files[0])}
                            />
                            <Button
                              as="label"
                              htmlFor={`logo-upload-${org.id}`}
                              colorScheme="teal"
                              size="sm"
                              isLoading={uploadingLogoId === org.id}
                              cursor="pointer"
                            >
                              Update Logo
                            </Button>
                            <Button
                              leftIcon={<FiSettings />}
                              colorScheme="blue"
                              size="sm"
                              onClick={() => handleOpenDefaultsModal(org)}
                            >
                              Manage Defaults
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}

          {/* Users Section */}
          {!isLoading && activeSection === 'users' && (
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Heading size="md">User Management</Heading>
                <Button 
                  colorScheme="blue" 
                  size="sm" 
                  onClick={handleViewUsersButtonClick} 
                  isLoading={isLoading}
                >
                  Refresh Users
                </Button>
              </Flex>
              
              {selectedUser && (
                <Box 
                  p={4} 
                  mb={4} 
                  bg="blue.50" 
                  borderRadius="md" 
                  borderLeft="4px solid" 
                  borderColor="blue.500"
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <HStack>
                      <Avatar size="sm" name={selectedUser.email} />
                      <Box>
                        <Text fontWeight="bold">Currently impersonating:</Text>
                        <Text>{selectedUser.email}</Text>
                      </Box>
                    </HStack>
                    <Button 
                      colorScheme="red" 
                      size="sm" 
                      onClick={handleStopImpersonation}
                      isLoading={isLoading}
                    >
                      Stop Impersonating
                    </Button>
                  </Flex>
                </Box>
              )}

              {users.length > 0 ? (
                <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('email')}
                        >
                          <Flex align="center">
                            Email {renderSortIcon('email')}
                          </Flex>
                        </Th>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('role')}
                        >
                          <Flex align="center">
                            Role {renderSortIcon('role')}
                          </Flex>
                        </Th>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('org_name')}
                        >
                          <Flex align="center">
                            Organization {renderSortIcon('org_name')}
                          </Flex>
                        </Th>
                        {/* New column for authorization status */}
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('auth_status')}
                        >
                          <Flex align="center">
                            Auth Status {renderSortIcon('auth_status')}
                          </Flex>
                        </Th>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('last_activity_date')}
                        >
                          <Flex align="center">
                            Last Activity {renderSortIcon('last_activity_date')}
                          </Flex>
                        </Th>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('note_count')}
                        >
                          <Flex align="center">
                            Notes {renderSortIcon('note_count')}
                          </Flex>
                        </Th>
                        <Th 
                          cursor="pointer" 
                          onClick={() => requestSort('case_count')}
                        >
                          <Flex align="center">
                            Cases {renderSortIcon('case_count')}
                          </Flex>
                        </Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {getSortedUsers().map((user) => (
                        <Tr key={user.id}>
                          <Td>
                            <HStack>
                              <Avatar size="xs" name={user.email} />
                              <Text>{user.email}</Text>
                            </HStack>
                          </Td>
                          <Td>
                            {user.role ? (
                              <Badge colorScheme={user.role === 'admin' ? 'red' : 'blue'}>
                                {user.role}
                              </Badge>
                            ) : 'N/A'}
                          </Td>
                          <Td>{user.org_name}</Td>
                          {/* Authorization status column */}
                          <Td>
                            {user.auth_status ? (
                              <Tooltip 
                                label={user.auth_status_date ? `Updated: ${new Date(user.auth_status_date).toLocaleString()}` : 'Status date unknown'} 
                                placement="top"
                              >
                                <HStack>
                                  <Badge 
                                    colorScheme={getStatusBadgeProps(user.auth_status).colorScheme}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Icon 
                                      as={getStatusBadgeProps(user.auth_status).icon} 
                                      mr={1} 
                                      boxSize="12px"
                                    />
                                    {user.auth_status}
                                  </Badge>
                                </HStack>
                              </Tooltip>
                            ) : (
                              <Badge colorScheme="gray">unknown</Badge>
                            )}
                          </Td>
                          <Td>
                            <Tooltip 
                              label={user.last_activity_date ? new Date(user.last_activity_date).toLocaleString() : 'No activity recorded'} 
                              placement="top"
                            >
                              <Text>{formatDate(user.last_activity_date)}</Text>
                            </Tooltip>
                          </Td>
                          <Td isNumeric>{user.note_count || 0}</Td>
                          <Td isNumeric>{user.case_count || 0}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button 
                                colorScheme="teal"
                                size="sm"
                                onClick={() => handleStartImpersonation(user.id)}
                                isDisabled={isImpersonating}
                                isLoading={isLoading}
                              >
                                Impersonate
                              </Button>
                              
                              {/* Status change menu */}
                              <Menu>
                                <MenuButton
                                  as={Button}
                                  size="sm"
                                  variant="outline"
                                  colorScheme="blue"
                                  isLoading={updatingStatus === user.id}
                                >
                                  <Icon as={FiMoreVertical} />
                                </MenuButton>
                                <MenuList>
                                  <MenuItem 
                                    icon={<Icon as={FiCheckCircle} color="green.500" />}
                                    onClick={() => updateUserAuthStatus(user.id, 'approved')}
                                    isDisabled={user.auth_status === 'approved'}
                                  >
                                    Approve User
                                  </MenuItem>
                                  <MenuItem 
                                    icon={<Icon as={FiAlertCircle} color="yellow.500" />}
                                    onClick={() => updateUserAuthStatus(user.id, 'pending')}
                                    isDisabled={user.auth_status === 'pending'}
                                  >
                                    Set to Pending
                                  </MenuItem>
                                  <MenuItem 
                                    icon={<Icon as={FiLock} color="red.500" />}
                                    onClick={() => updateUserAuthStatus(user.id, 'rejected')}
                                    isDisabled={user.auth_status === 'rejected'}
                                  >
                                    Reject User
                                  </MenuItem>
                                  <Divider />
                                  <MenuItem 
                                    icon={<Icon as={FiUsers} color="blue.500" />}
                                    onClick={() => updateUserRole(user.id, 'pilot')}
                                    isDisabled={user.role === 'pilot'}
                                  >
                                    Set Role: Pilot
                                  </MenuItem>
                                  <MenuItem 
                                    icon={<Icon as={FiUsers} color="gray.500" />}
                                    onClick={() => updateUserRole(user.id, null)}
                                    isDisabled={!user.role || user.role === 'admin' || user.role === 'superadmin'}
                                  >
                                    Set Role: Standard
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Box p={4} textAlign="center" borderRadius="md" borderWidth="1px">
                  <Text>No users found</Text>
                </Box>
              )}
            </Box>
          )}

          {/* Chat Details Section */}
          {!isLoading && activeSection === 'chatDetails' && (
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Heading size="md">Chat Details</Heading>
                <Button 
                  colorScheme="blue" 
                  size="sm" 
                  onClick={handleChatDetailsButtonClick}
                  isLoading={isLoading}
                >
                  Refresh Chat Details
                </Button>
              </Flex>
              
              {chatDetails && chatDetails.chat_details && chatDetails.chat_details.length > 0 ? (
                <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th>Chat Info</Th>
                        <Th>Messages</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {chatDetails.chat_details
                        .sort((a, b) => new Date(b.chat_created_at) - new Date(a.chat_created_at))
                        .map((row, index) => (
                          <Tr key={index}>
                            <Td width="30%">
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="bold">{row.email}</Text>
                                <Text fontSize="sm" color="gray.500">ID: {row.chat_id}</Text>
                                <Text fontSize="sm">{new Date(row.chat_created_at).toLocaleString()}</Text>
                                {row.chat_additional_data && (
                                  <Text fontSize="xs" color="gray.500">{row.chat_additional_data}</Text>
                                )}
                              </VStack>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={2} divider={<Divider />}>
                                {row.messages.map((message, messageIndex) => (
                                  <Box 
                                    key={messageIndex} 
                                    p={2} 
                                    borderRadius="md" 
                                    bg={messageIndex % 2 === 0 ? 'blue.50' : 'green.50'}
                                    maxWidth="100%"
                                  >
                                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                                      {messageIndex % 2 === 0 ? 'User' : 'Assistant'}
                                    </Text>
                                    <Text fontSize="sm">{message}</Text>
                                  </Box>
                                ))}
                              </VStack>
                            </Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Box p={4} textAlign="center" borderRadius="md" borderWidth="1px">
                  <Text>No chat details available.</Text>
                </Box>
              )}
            </Box>
          )}
        </GridItem>
      </Grid>

      {/* Manage Defaults Modal */}
      <Modal isOpen={isDefaultsModalOpen} onClose={onDefaultsModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manage Defaults for {selectedOrgForDefaults?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isDefaultsLoading ? (
              <Flex justify="center" align="center" height="300px">
                <Spinner size="xl" />
              </Flex>
            ) : (
              <VStack spacing={6} align="stretch">
                {/* Organization Default */}
                <FormControl>
                  <FormLabel fontWeight="bold">Organization-Wide Default</FormLabel>
                  <Select
                    placeholder="No organization default"
                    value={orgDefaultClientId}
                    onChange={(e) => setOrgDefaultClientId(e.target.value)}
                  >
                    {orgClients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <Divider />

                {/* User-Specific Defaults */}
                <Box>
                  <Heading size="sm" mb={4}>User-Specific Overrides</Heading>
                  <Box maxH="400px" overflowY="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>User</Th>
                          <Th>Default Configuration</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {orgUsers.map(user => (
                          <Tr key={user.id}>
                            <Td>{user.email}</Td>
                            <Td>
                              <Select
                                placeholder="(Use Organization Default)"
                                value={userDefaultClientIds[user.id] || ''}
                                onChange={(e) => handleUserDefaultChange(user.id, e.target.value)}
                              >
                                {orgClients.map(client => (
                                  <option key={client.id} value={client.id}>
                                    {client.name}
                                  </option>
                                ))}
                              </Select>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDefaultsModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveDefaults}
              isLoading={isSavingDefaults}
              isDisabled={isDefaultsLoading}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Admin;